import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { dateSendToBackend } from "../../lib/constant";
import {
  register,
  verifyMobileByOTP,
  verifyMobileOTP,
} from "../../services/AuthServices";
import OTPInput from "react-otp-input";
import "../Login/Auth.css";

const MainSection = () => {
  const [isDisable, setisDisable] = useState(false);
  const [resendLoading, setresendLoading] = useState(true);
  const initialTime = 120;
  const location = useLocation();
  const registerData = location?.state;

  const [seconds, setSeconds] = useState(initialTime);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (seconds > 0) {
      const timerId = setInterval(() => {
        setSeconds(seconds - 1);
      }, 1000);
      return () => clearInterval(timerId);
    } else {
      setresendLoading(false);
    }
  }, [seconds]);

  const submitClicked = async (data) => {
    setisDisable(true);
    let formdata = new FormData();
    formdata.append("otp", data);
    formdata.append("email", registerData?.email);

    await verifyMobileOTP(formdata)
      .then((response) => {
        registerUser();
      })
      .catch((error) => {
        setisDisable(false);
        if (error?.response?.status == 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error?.response?.data?.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", { type: "error" });
      });
  };

  const verifyEmail = async () => {
    setisDisable(true);
    let formdata = new FormData();
    formdata.append("email", registerData?.email);
    await verifyMobileByOTP(formdata)
      .then((response) => {
        setisDisable(false);
        toast("OTP successfully sent to your email", { type: "success" });
      })
      .catch((error) => {
        setisDisable(false);
        if (error?.response?.status == 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error.response.data.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", { type: "error" });
      });
  };

  const registerUser = async () => {
    setisDisable(true);
    let formdata = new FormData();
    formdata.append("firstName", registerData?.firstName);
    formdata.append("lastName", registerData?.lastName);
    formdata.append("email", registerData?.email);
    formdata.append("password", registerData?.password);
    formdata.append("company", registerData?.company);
    formdata.append("address", registerData?.address);
    formdata.append("address2", registerData?.address2);
    formdata.append("city", registerData?.city);
    formdata.append("state", registerData?.state);
    formdata.append("zip", registerData?.zip);
    formdata.append("phone", registerData?.phone);
    formdata.append("fax", registerData?.fax);
    formdata.append("dateOfBirth", dateSendToBackend(registerData?.dateOfBirth));
    formdata.append("logo", registerData?.logo);
    formdata.append("isEmailSpeacialOffer", registerData?.isEmailSpeacialOffer ? 1 : 0);
    await register(formdata)
      .then((response) => {
        setisDisable(false);
        toast("Account created successfully!", { type: "success" });
        navigate("/login");
      })
      .catch((error) => {
        setisDisable(false);
        if (error?.response?.status == 422)
          toast(error?.response?.data?.message, { type: "error" });
        else if (error?.response?.status == 500)
          toast(error.response.data.message, { type: "error" });
        else
          toast(error?.response?.data?.message || "Something went wrong", { type: "error" });
      });
  };

  const { handleSubmit, setFieldValue, values } = useFormik({
    initialValues: { otp: "" },
    onSubmit: async () => {
      if (values?.otp?.length === 6 && Number(values?.otp)) {
        submitClicked(values?.otp);
      } else {
        toast("Please enter a valid 6-digit OTP", { type: "error" });
      }
    },
    onReset: () => {},
  });

  const handleInputChange = (data) => {
    setFieldValue("otp", data);
  };

  const handleResend = () => {
    verifyEmail();
    setSeconds(initialTime);
    setresendLoading(true);
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <div className="dp-auth-container">
      <div className="dp-auth-card" style={{ maxWidth: '900px' }}>
        {/* Left Side - Illustration */}
        <div className="dp-auth-left">
          <img
            src="https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=400&h=400&fit=crop&auto=format"
            alt="OTP Verification"
            className="dp-auth-illustration"
            style={{ borderRadius: '20px', objectFit: 'cover' }}
          />
          <div className="dp-auth-brand">
            <h2>Verify Your Email</h2>
            <p>We've sent a 6-digit verification code to your email address.</p>
            <div className="dp-auth-features">
              <div className="dp-auth-feature">
                <i className="fas fa-envelope-open-text"></i>
                <span>Check your inbox</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-shield-alt"></i>
                <span>Secure verification</span>
              </div>
              <div className="dp-auth-feature">
                <i className="fas fa-clock"></i>
                <span>Code expires in 2 minutes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="dp-auth-right">
          <div className="dp-auth-header">
            <h1>OTP Verification</h1>
            <p>Enter the 6-digit code sent to your email</p>
          </div>

          <form className="dp-auth-form" onSubmit={handleSubmit}>
            {/* OTP Input */}
            <div className="dp-otp-container">
              <OTPInput
                value={values?.otp}
                onChange={handleInputChange}
                numInputs={6}
                isInputNum={true}
                renderSeparator={<span className="dp-otp-separator">-</span>}
                renderInput={(props) => <input {...props} />}
                inputStyle="dp-otp-input"
                focusStyle="dp-otp-input-focus"
                containerStyle="dp-otp-input-container"
              />
            </div>

            {/* Timer & Resend */}
            <div className="dp-otp-timer">
              {seconds > 0 ? (
                <p className="dp-otp-countdown">
                  <i className="far fa-clock"></i>
                  Code expires in <span>{formatTime(seconds)}</span>
                </p>
              ) : (
                <p className="dp-otp-expired">Code has expired</p>
              )}
              <button
                type="button"
                className={`dp-otp-resend ${resendLoading ? 'disabled' : ''}`}
                onClick={!resendLoading ? handleResend : undefined}
                disabled={resendLoading}
              >
                <i className="far fa-redo"></i>
                Resend Code
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="dp-auth-submit"
              disabled={isDisable}
            >
              {isDisable ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Verifying...
                </>
              ) : (
                <>
                  <i className="far fa-check-circle"></i>
                  Verify & Continue
                </>
              )}
            </button>

            {/* Footer */}
            <div className="dp-auth-footer">
              <p>
                Didn't receive the code?{" "}
                <span onClick={() => navigate("/register")}>Try again</span>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MainSection;
